// BILLING-PLANS: the reads of the Plan side of Billing and of Operator › Billing, and the types
// the screens use, straight from the route contracts (packages/schema/src/contracts/api/
// billing.ts). Calls go through the SDK's `call(id, …)`, typed by the same contracts. The
// wallet's reads are WALLET-P1's (billing-queries.ts); the keys never overlap.
import type { OperationOutput } from "@companyos/sdk";
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type BillingOverview = OperationOutput<"billing.overview">;
export type SubscriptionView = NonNullable<BillingOverview["subscription"]>;
export type UpcomingInvoice = NonNullable<SubscriptionView["upcoming"]>;
export type InvoiceLine = UpcomingInvoice["lines"][number];
export type BillingAgentLine = BillingOverview["agents"][number];
export type InvoiceView = OperationOutput<"billing.invoices.get">;
export type InvoiceDisplayStatus = InvoiceView["display_status"];
export type Quote = OperationOutput<"billing.quotes.accept">;
export type DraftLine = Quote["lines"][number];
export type ProjectView = OperationOutput<"operator.billing.projects.create">;
export type PriceLists = OperationOutput<"operator.billing.prices">;
export type PlatformPlanRow = PriceLists["platform"][number];
export type MaintenancePlanRow = PriceLists["maintenance"][number];
export type ChargeCatalogItem = PriceLists["catalog"][number];
export type TemplateBillingRow = PriceLists["templates"][number];
export type BillingDefaults = PriceLists["defaults"];
export type OperatorCompanyBilling = OperationOutput<"operator.billing.company">;
export type DiscountView = OperatorCompanyBilling["discounts"][number];
export type OperatorBillingCompanyRow =
  OperationOutput<"operator.billing.companies">["data"][number];
export type InvoicesAndQuotes = OperationOutput<"operator.billing.invoices.list">;
export type OperatorInvoiceRow = InvoicesAndQuotes["invoices"][number];
export type OperatorQuoteRow = InvoicesAndQuotes["quotes"][number];
export type MaintenanceTier = MaintenancePlanRow["tier"];
export type PaymentMethod = BillingDefaults["payment_methods"][number];

const PAGE = 25;

export const planKeys = {
  all: (co: string) => ["company", co, "billing"] as const,
  overview: (co: string) => ["company", co, "billing", "overview"] as const,
  invoices: (co: string) => ["company", co, "billing", "invoices"] as const,
  quotes: (co: string) => ["company", co, "billing", "quotes"] as const,
  projects: (co: string) => ["company", co, "billing", "projects"] as const,
};

export const opBillingKeys = {
  all: () => ["operator", "billing"] as const,
  prices: () => ["operator", "billing", "prices"] as const,
  companies: () => ["operator", "billing", "companies"] as const,
  invoices: () => ["operator", "billing", "invoices"] as const,
  company: (id: string) => ["operator", "billing", "company", id] as const,
  quotes: (id: string) => ["operator", "billing", "company", id, "quotes"] as const,
};

export const billingOverviewQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: planKeys.overview(co),
    queryFn: () => api.client.call("billing.overview", { params: { co } }),
  });

export const billingInvoicesQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: planKeys.invoices(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.call("billing.invoices.list", {
        params: { co },
        query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const billingQuotesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: planKeys.quotes(co),
    queryFn: () => api.client.call("billing.quotes.list", { params: { co }, query: { limit: 50 } }),
  });

export const billingProjectsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: planKeys.projects(co),
    queryFn: () => api.client.call("billing.projects.list", { params: { co } }),
  });

export const priceListsQuery = (api: Api) =>
  queryOptions({
    queryKey: opBillingKeys.prices(),
    queryFn: () => api.client.call("operator.billing.prices", {}),
  });

export const opCompaniesQuery = (api: Api) =>
  queryOptions({
    queryKey: opBillingKeys.companies(),
    queryFn: () => api.client.call("operator.billing.companies", {}),
  });

/** The operator's invoices; a status is filtered by the server before its limit. */
export const opInvoicesQuery = (
  api: Api,
  status?: "draft" | "open" | "overdue" | "paid" | "void" | "uncollectible",
  company?: string,
) =>
  infiniteQueryOptions({
    queryKey: [...opBillingKeys.invoices(), status ?? "all", company ?? "all"] as const,
    // the last list stays (filtered here too) while the server's filtered one loads
    placeholderData: keepPreviousData,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.call("operator.billing.invoices.list", {
        query: {
          limit: 50,
          ...(status ? { status } : {}),
          ...(company ? { company } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const opCompanyQuery = (api: Api, companyId: string) =>
  queryOptions({
    queryKey: opBillingKeys.company(companyId),
    queryFn: () =>
      api.client.call("operator.billing.company", { params: { company_id: companyId } }),
  });
